import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router"
import { useAuthStore } from "@/stores/auth"
import { analytics } from "@/analytics"

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginView.vue"),
    meta: { requiresAuth: false }
  },
  {
    path: "/",
    component: () => import("@/views/AppLayout.vue"),
    meta: { requiresAuth: true },
    children: [
      { path: "", redirect: "/bookings" },
      {
        path: "bookings",
        name: "BookingsList",
        component: () => import("@/views/BookingsListView.vue")
      },
      {
        path: "bookings/new",
        name: "BookingCreate",
        component: () => import("@/views/BookingFormView.vue")
      },
      {
        // Mobile-only full-screen address picker for the booking form
        path: "bookings/new/address/:stop(pickup|delivery)",
        name: "BookingAddressPicker",
        component: () => import("@/views/AddressPickerView.vue"),
        props: true
      },
      {
        // The order during its undo window: the booking's page, before the central has it
        path: "bookings/sending",
        name: "BookingSending",
        component: () => import("@/views/BookingDetailView.vue"),
        props: { sending: true }
      },
      {
        path: "bookings/:id(\\d+)",
        name: "BookingDetail",
        component: () => import("@/views/BookingDetailView.vue"),
        props: route => ({ id: Number(route.params.id), month: typeof route.query.month === "string" ? route.query.month : undefined })
      },
      {
        path: "bookings/:id(\\d+)/receipt",
        name: "BookingReceipt",
        component: () => import("@/views/ReceiptView.vue"),
        props: route => ({ id: Number(route.params.id), month: typeof route.query.month === "string" ? route.query.month : undefined })
      },
      {
        path: "bookings/:id(\\d+)/track",
        name: "BookingTrack",
        component: () => import("@/views/TrackView.vue"),
        props: route => ({ id: Number(route.params.id), month: typeof route.query.month === "string" ? route.query.month : undefined })
      },
      {
        path: "addresses",
        name: "AddressesList",
        component: () => import("@/views/AddressesView.vue")
      },
      {
        path: "addresses/new",
        name: "AddressCreate",
        component: () => import("@/views/AddressFormView.vue")
      },
      {
        path: "addresses/:id",
        name: "AddressEdit",
        component: () => import("@/views/AddressFormView.vue"),
        props: true
      },
      {
        path: "profile",
        name: "Profile",
        component: () => import("@/views/ProfileView.vue")
      }
    ]
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: "/bookings"
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  }
})

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  if (requiresAuth && !authStore.isLoggedIn) {
    next({ name: "Login", query: { redirect: to.fullPath } })
  } else if (to.name === "Login" && authStore.isLoggedIn) {
    next({ name: "BookingsList" })
  } else {
    next()
  }
})

router.afterEach(to => {
  analytics.trackPageView((to.name as string) || "Unknown", to.fullPath)
})

export default router
